/**
 * AP-34 — Eingangsrechnungen: der Vertrag zwischen Backend und Oberfläche.
 *
 * Spezifikation: `docs/Arbeitspakete/AP-34/` — `AP-34_Eingangsrechnungen.md`, Nachtrag 1 und
 * Nachtrag 2; bei Widerspruch gilt der spätere Nachtrag.
 *
 * Phase B: Aufzählungen, Vorgaben ab Werk und die Eingangsnummer. Prüfung (10.), Abgleich (9.)
 * und die Einstufung der Lesung (Nachtrag 2, 5.2) kommen mit ihren Phasen dazu.
 */

// ── Aufzählungen ────────────────────────────────────────────────────────────

export const INCOMING_INVOICE_KINDS = ['INVOICE', 'DOWN_PAYMENT_INVOICE', 'PARTIAL_INVOICE', 'FINAL_INVOICE', 'CREDIT_NOTE'] as const
export type IncomingInvoiceKind = (typeof INCOMING_INVOICE_KINDS)[number]

export const INCOMING_INVOICE_STATUSES = ['UNCAPTURED', 'DRAFT', 'IN_APPROVAL', 'REJECTED', 'RELEASED', 'EXPORTED', 'CANCELLED'] as const
export type IncomingInvoiceStatus = (typeof INCOMING_INVOICE_STATUSES)[number]

/** 5.2 — in diesen Ständen ist die Rechnung bearbeitbar. */
export const INCOMING_EDITABLE_STATUSES: readonly IncomingInvoiceStatus[] = ['UNCAPTURED', 'DRAFT', 'REJECTED']

export const INCOMING_PAY_STATUSES = ['NONE', 'OPEN', 'PARTIALLY_PAID', 'PAID', 'OVERDUE'] as const
export type IncomingPayStatus = (typeof INCOMING_PAY_STATUSES)[number]

/** Woher die Rechnung kam. `INBOX`: aus dem Posteingang übergeben (Nachtrag 1, 6.1). */
export const INCOMING_INVOICE_SOURCES = ['MANUAL', 'EINVOICE', 'AI_CAPTURE', 'INBOX'] as const
export type IncomingInvoiceSource = (typeof INCOMING_INVOICE_SOURCES)[number]

export const INCOMING_LINE_TYPES = ['ITEM', 'CHARGE', 'DEDUCTION', 'TEXT'] as const
export type IncomingLineType = (typeof INCOMING_LINE_TYPES)[number]

/** Zeilen, die einen Wert tragen — nur sie haben Kostenziel, Steuercode und Konto. */
export const INCOMING_VALUE_LINE_TYPES: readonly IncomingLineType[] = ['ITEM', 'CHARGE', 'DEDUCTION']

/** Nachtrag 2, 2.1 — genau ein Kostenziel je Wertzeile. */
export const INCOMING_COST_TARGETS = ['PROJECT', 'STOCK', 'OVERHEAD'] as const
export type IncomingCostTarget = (typeof INCOMING_COST_TARGETS)[number]

export const INCOMING_MATCH_STATUSES = ['NONE', 'MATCHED', 'DEVIATION'] as const
export type IncomingMatchStatus = (typeof INCOMING_MATCH_STATUSES)[number]

export const INCOMING_TAX_PROCEDURES = [
  'DOMESTIC',
  'REVERSE_CHARGE_CONSTRUCTION',
  'REVERSE_CHARGE_OTHER',
  'INTRA_EU_ACQUISITION',
  'IMPORT',
  'SMALL_BUSINESS',
  'NO_INPUT_TAX',
] as const
export type IncomingTaxProcedure = (typeof INCOMING_TAX_PROCEDURES)[number]

/** 10.2 — Freigabeanlässe; eine Regel nennt die, auf die sie anspricht (11.2). */
export const INCOMING_APPROVAL_REASONS = [
  'NO_ORDER',
  'QUANTITY_DEVIATION',
  'PRICE_DEVIATION',
  'UNIT_MISMATCH',
  'UNMATCHED_CHARGES',
  'SUM_MISMATCH',
  'IBAN_CHANGED',
  'FORMAL_DEFECT',
  'AMOUNT_ABOVE_LIMIT',
  'AGH_NOT_LISTED',
] as const
export type IncomingApprovalReason = (typeof INCOMING_APPROVAL_REASONS)[number]

/** 11.1 — wann ein Freigabelauf startet. */
export const INCOMING_APPROVAL_MODES = ['DEVIATIONS', 'ALWAYS', 'NEVER'] as const
export type IncomingApprovalMode = (typeof INCOMING_APPROVAL_MODES)[number]

/** 11.3 mit Nachtrag 2, 4. — `COST_OWNER` ersetzt den Projektleiter. */
export const INCOMING_APPROVER_KINDS = ['COST_OWNER', 'ORDER_CLERK', 'USER', 'ROLE'] as const
export type IncomingApproverKind = (typeof INCOMING_APPROVER_KINDS)[number]

export const INCOMING_EXPORT_TARGETS = ['BMD_NTCS_CSV', 'GENERIC_CSV', 'XLSX_LIST', 'DOCUMENT_ARCHIVE'] as const
export type IncomingExportTarget = (typeof INCOMING_EXPORT_TARGETS)[number]

export const INCOMING_BOOKKEEPING_MODES = ['POSTED', 'UNPOSTED'] as const
export type IncomingBookkeepingMode = (typeof INCOMING_BOOKKEEPING_MODES)[number]

// ── Vorgaben ab Werk (18., Nachtrag 2 3.1) ──────────────────────────────────

export interface IncomingTaxCodeVorgabe {
  key: string
  label: string
  rate: number
  procedure: IncomingTaxProcedure
  inputTaxDeductible: boolean
}

/** 18. — mitgelieferte Steuercodes; der BMD-Code je Schlüssel steht im Exportprofil, nicht hier. */
export const INCOMING_TAX_CODE_VORGABEN: readonly IncomingTaxCodeVorgabe[] = [
  { key: 'VST20', label: 'Vorsteuer 20 %', rate: 20, procedure: 'DOMESTIC', inputTaxDeductible: true },
  { key: 'VST13', label: 'Vorsteuer 13 %', rate: 13, procedure: 'DOMESTIC', inputTaxDeductible: true },
  { key: 'VST10', label: 'Vorsteuer 10 %', rate: 10, procedure: 'DOMESTIC', inputTaxDeductible: true },
  { key: 'VST0', label: 'Ohne Vorsteuer (0 %)', rate: 0, procedure: 'DOMESTIC', inputTaxDeductible: true },
  { key: 'RC_BAU20', label: 'Reverse Charge Bauleistung 20 %', rate: 20, procedure: 'REVERSE_CHARGE_CONSTRUCTION', inputTaxDeductible: true },
  { key: 'RC_SONST20', label: 'Reverse Charge sonstige Leistung 20 %', rate: 20, procedure: 'REVERSE_CHARGE_OTHER', inputTaxDeductible: true },
  { key: 'IGE20', label: 'Innergemeinschaftlicher Erwerb 20 %', rate: 20, procedure: 'INTRA_EU_ACQUISITION', inputTaxDeductible: true },
  { key: 'IGE10', label: 'Innergemeinschaftlicher Erwerb 10 %', rate: 10, procedure: 'INTRA_EU_ACQUISITION', inputTaxDeductible: true },
  { key: 'EUST', label: 'Einfuhrumsatzsteuer', rate: 20, procedure: 'IMPORT', inputTaxDeductible: true },
  { key: 'KU', label: 'Kleinunternehmer (keine Vorsteuer)', rate: 0, procedure: 'SMALL_BUSINESS', inputTaxDeductible: false },
  { key: 'NVST20', label: 'Vorsteuer 20 % nicht abzugsfähig', rate: 20, procedure: 'NO_INPUT_TAX', inputTaxDeductible: false },
]

/** Nachtrag 2, 3.1 — mitgelieferte Gemeinkosten-Kategorien, änderbar. */
export const INCOMING_OVERHEAD_VORGABEN: readonly string[] = [
  'Telefon und Internet',
  'Büromaterial',
  'Versicherungen',
  'Abgaben und Gebühren',
  'Miete und Betriebskosten',
  'Fahrzeuge',
  'Energie',
  'Beratung',
  'Sonstiges',
]

export interface IncomingApprovalRuleVorgabe {
  name: string
  triggers: readonly IncomingApprovalReason[]
  stages: readonly { approverKind: IncomingApproverKind; decision: 'ANY' | 'ALL' }[]
}

/** 11.2 mit Nachtrag 2, 4. — mitgelieferte Regeln; die erste passende gewinnt. */
export const INCOMING_APPROVAL_RULE_VORGABEN: readonly IncomingApprovalRuleVorgabe[] = [
  {
    name: 'Abweichung zur Bestellung',
    triggers: ['QUANTITY_DEVIATION', 'PRICE_DEVIATION', 'UNIT_MISMATCH', 'UNMATCHED_CHARGES'],
    stages: [{ approverKind: 'ORDER_CLERK', decision: 'ANY' }],
  },
  { name: 'Ohne Bestellung', triggers: ['NO_ORDER'], stages: [{ approverKind: 'COST_OWNER', decision: 'ANY' }] },
  {
    // Die Rolle wählt der Mandant; bis dahin trägt der Rückfall-Freigeber die Aufgabe.
    name: 'Kontrolle',
    triggers: ['SUM_MISMATCH', 'IBAN_CHANGED', 'FORMAL_DEFECT', 'AGH_NOT_LISTED'],
    stages: [{ approverKind: 'ROLE', decision: 'ANY' }],
  },
]

/** 13.4 — Felder des BMD-Buchungsimports: Feld → Spaltenname. Vor `BETA` mit einem echten BMD-Mandanten abzugleichen (27.3). */
export const INCOMING_BMD_SPALTEN_VORGABE: readonly { feld: string; spalte: string }[] = [
  { feld: 'satzart', spalte: 'satzart' },
  { feld: 'konto', spalte: 'konto' },
  { feld: 'gegenkonto', spalte: 'gkonto' },
  { feld: 'belegnummer', spalte: 'belegnr' },
  { feld: 'belegdatum', spalte: 'belegdatum' },
  { feld: 'buchungsdatum', spalte: 'buchdatum' },
  { feld: 'buchungssymbol', spalte: 'buchsymbol' },
  { feld: 'buchungscode', spalte: 'buchcode' },
  { feld: 'steuerprozent', spalte: 'prozent' },
  { feld: 'steuercode', spalte: 'steuercode' },
  { feld: 'betrag', spalte: 'betrag' },
  { feld: 'steuer', spalte: 'steuer' },
  { feld: 'text', spalte: 'text' },
  { feld: 'externeBelegnummer', spalte: 'extbelegnr' },
  { feld: 'zahlungsziel', spalte: 'zziel' },
  { feld: 'skontotage', spalte: 'skontotage' },
  { feld: 'skontoprozent', spalte: 'skontopz' },
  { feld: 'uid', spalte: 'uidnr' },
]

// ── Eingangsnummer (7.7) ────────────────────────────────────────────────────

/** Vorgabe des Musters — **nur Ziffern**, weil BMD die Belegnummer numerisch führt (27.3). */
export const INCOMING_NUMBER_PATTERN_VORGABE = '{jahr2}{nummer:5}'

const PLATZHALTER = /\{(jahr2|jahr|nummer)(?::(\d{1,2}))?\}/g

/**
 * Baut die Eingangsnummer aus Muster, Jahr und Laufnummer.
 *
 * Platzhalter: `{jahr}` (vierstellig), `{jahr2}` (zweistellig), `{nummer}` und `{nummer:5}` (aufgefüllt;
 * darüber einfach länger). Ein Muster ohne `{nummer}` ergäbe für jede Rechnung dieselbe Nummer — dann
 * wird die Laufnummer angehängt, statt dass die zweite Rechnung am Unique-Index scheitert.
 */
export function eingangsnummer(muster: string, jahr: number, laufnummer: number): string {
  const m = muster?.trim() || INCOMING_NUMBER_PATTERN_VORGABE
  let hatNummer = false
  const text = m.replace(PLATZHALTER, (_, name: string, stellen?: string) => {
    if (name === 'jahr') return String(jahr)
    if (name === 'jahr2') return String(jahr % 100).padStart(2, '0')
    hatNummer = true
    return String(laufnummer).padStart(stellen ? Number(stellen) : 1, '0')
  })
  return hatNummer ? text : `${text}${laufnummer}`
}

/** Prüft ein Muster für die Einstellungen: bekannte Platzhalter, eine Laufnummer, keine offenen Klammern. */
export function eingangsnummerMusterFehler(muster: string): 'LEER' | 'OHNE_NUMMER' | 'UNBEKANNTER_PLATZHALTER' | null {
  const m = muster?.trim()
  if (!m) return 'LEER'
  if (!/\{nummer(?::\d{1,2})?\}/.test(m)) return 'OHNE_NUMMER'
  if (/[{}]/.test(m.replace(PLATZHALTER, ''))) return 'UNBEKANNTER_PLATZHALTER'
  return null
}

/**
 * 7.1 — die Rechnungsnummer des Lieferanten für die Dublette: Großbuchstaben, nur Buchstaben und
 * Ziffern. „RE-2026/0815", „re 2026 0815" und „RE20260815" sind dieselbe Rechnung.
 */
export function rechnungsnummerNormal(nummer: string | null | undefined): string | null {
  const n = (nummer ?? '').toUpperCase().replace(/[^A-Z0-9ÄÖÜ]/g, '')
  return n || null
}
